import type { JSX } from 'react';
import { FieldValues, Path } from 'react-hook-form';

import { FieldController, FieldControllerProps } from '../FieldController';
import { ControllerProps as DateControllerProps } from '../FieldDate/FieldDateControlled';
import { createDateValidator } from '../FieldDate/FieldDateControlled';
import { FieldDatePicker, FieldDatePickerProps } from './FieldDatePicker';

export interface FieldDatePickerControlledProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<FieldControllerProps<FieldDatePickerProps, TFieldValues, TName>, 'Field'> {
    required?: boolean;
    minDate?: string;
    maxDate?: string;
    inFuture?: boolean;
    inFutureOrToday?: boolean;
    inPast?: boolean;
    inPastOrToday?: boolean;
}

/** `FieldDatePicker` bound to RHF, with `FieldDateControlled`'s API, so swapping the import migrates. */
export function FieldDatePickerControlled<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
    required,
    minDate,
    maxDate,
    inFuture,
    inFutureOrToday,
    inPast,
    inPastOrToday,
    validate,
    ...props
}: FieldDatePickerControlledProps<TFieldValues, TName>): JSX.Element {
    const dateOpts: Pick<
        DateControllerProps,
        'required' | 'minDate' | 'maxDate' | 'inFuture' | 'inFutureOrToday' | 'inPast' | 'inPastOrToday' | 'validate'
    > = {
        required,
        minDate,
        maxDate,
        inFuture,
        inFutureOrToday,
        inPast,
        inPastOrToday,
        validate,
    };

    return <FieldController Field={FieldDatePicker} validate={createDateValidator(dateOpts)} {...props} />;
}
